// AI-generated code - 2026-02-02T13:50:40Z
import {
    EnumOrderType,
    EnumProcessState,
    type ApiOrder,
    type ApiOrderPosition,
    type ApiPrintOption,
    type ApiWeighingSlip,
} from '@Model';
import { isLinked, type AsyncStateUpdate, type Awaitable } from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';
import { useClientNavigate } from '../../Routing/useClientNavigate';
import { EnumWeighing } from './useWeighing';

export interface WeighingModalState {
    orderPositionId: string | undefined;
    weighingSlipId: string | undefined;
    mode: EnumWeighing;
}

interface UseWeighingActionsParams {
    apiOrder: ApiOrder;
    weighingSlipsWithoutGross: string[];
    weighingSlipsWithoutTare: string[];
    onStartWeighing: () => void;
    addPosition: () => Promise<ApiOrderPosition | undefined>;

    setCurrentPrintOption: (option: ApiPrintOption | undefined) => void;
    openCashPaymentModal: () => void;
    saveAndCommit: (data?: ApiOrder) => Awaitable<boolean>;
    requestUpdateState: AsyncStateUpdate<ApiOrder>;
    deletePositionsHandler: (ids: string[]) => void;
}

export function useWeighingActions({
    apiOrder,
    weighingSlipsWithoutGross,
    weighingSlipsWithoutTare,
    onStartWeighing,
    addPosition,
    setCurrentPrintOption,
    openCashPaymentModal,
    saveAndCommit,
    requestUpdateState,
    deletePositionsHandler,
}: UseWeighingActionsParams) {
    const navigate = useClientNavigate();
    const [weighingModalState, setWeighingModalState] = useState<WeighingModalState>();
    const isCashPayment = apiOrder.type === EnumOrderType.Cash;

    const positionInWeighing = apiOrder.positions.find((pos) => pos.id === weighingModalState?.orderPositionId);

    const weighInNewPosition = useCallback(async () => {
        const pos = await addPosition();
        if (pos !== undefined) {
            setWeighingModalState({
                mode: EnumWeighing.Gross,
                orderPositionId: pos.id,
                weighingSlipId: undefined,
            });
        }
    }, [addPosition, setWeighingModalState]);

    const startWeighingIn = useCallback(async () => {
        onStartWeighing();

        const positionsForWeighingIn = apiOrder.positions.filter(
            (pos) =>
                pos.weighing_slips.length === 0 ||
                pos.weighing_slips.some((ws) => weighingSlipsWithoutGross.includes(ws.id)),
        );

        if (positionsForWeighingIn.length === 0) {
            await weighInNewPosition();
            return;
        }

        if (positionsForWeighingIn.length === 1) {
            const pos = positionsForWeighingIn[0];
            const weighingSlips = pos.weighing_slips.filter((ws) => weighingSlipsWithoutGross.includes(ws.id));
            if (weighingSlips.length < 2) {
                const firstWeighingSlip: ApiWeighingSlip | undefined = weighingSlips[0];
                setWeighingModalState({
                    mode: EnumWeighing.Gross,
                    orderPositionId: pos.id,
                    weighingSlipId: firstWeighingSlip?.id,
                });
                return;
            }
        }

        setWeighingModalState({
            mode: EnumWeighing.Gross,
            orderPositionId: undefined,
            weighingSlipId: undefined,
        });
    }, [apiOrder.positions, weighingSlipsWithoutGross, onStartWeighing, weighInNewPosition, setWeighingModalState]);

    const startWeighingOut = useCallback(async () => {
        onStartWeighing();

        const positionsForWeighingOut = apiOrder.positions
            .filter((pos) => pos.weighing_slips.some((ws) => weighingSlipsWithoutTare.includes(ws.id)))
            .flatMap((pos) => pos.weighing_slips.map((ws) => ({ positionId: pos.id, weighingSlipId: ws.id })));

        if (positionsForWeighingOut.length === 1) {
            const positionAndWeighingSlip = positionsForWeighingOut[0];

            setWeighingModalState({
                mode: EnumWeighing.Tare,
                orderPositionId: positionAndWeighingSlip.positionId,
                weighingSlipId: positionAndWeighingSlip.weighingSlipId,
            });
            return;
        }

        setWeighingModalState({
            mode: EnumWeighing.Tare,
            orderPositionId: undefined,
            weighingSlipId: undefined,
        });
    }, [apiOrder.positions, weighingSlipsWithoutTare, onStartWeighing]);

    const saveAndNavigateToHofliste = useCallback(async () => {
        const saveSuccessful = await saveAndCommit();
        if (!saveSuccessful) {
            return false;
        }

        navigate('../list?mode=hofliste');
        return true;
    }, [saveAndCommit, navigate]);

    const approveAndPrint = useCallback(async () => {
        setWeighingModalState(undefined);

        const saveSuccessful = await saveAndCommit();
        if (!saveSuccessful) {
            return false;
        }

        const updated = await requestUpdateState(async (latest, saveData) => {
            return saveData({
                ...latest,
                process_state: EnumProcessState.Approved,
            });
        });
        if (!updated) {
            return false;
        }

        if (isCashPayment) {
            openCashPaymentModal();
        } else {
            setCurrentPrintOption({
                id: 'weighing_slip',
                category: 'order',
                display_string: 'Wiegeschein',
                related_object_id: apiOrder.id,
                existing_preview_id: undefined,
                attachments: [],
                file_name: 'weighing-slip.pdf',
            });
        }
    }, [
        setWeighingModalState,
        saveAndCommit,
        requestUpdateState,
        isCashPayment,
        openCashPaymentModal,
        setCurrentPrintOption,
        apiOrder.id,
    ]);

    const closeWeighingModal = useCallback(() => {
        setWeighingModalState(undefined);

        if (positionInWeighing === undefined) {
            return;
        }

        if (isLinked(positionInWeighing.service)) {
            return;
        }

        const weighingSlipsInWeighing = positionInWeighing.weighing_slips;
        const hasWeighingSlipWithWeight = weighingSlipsInWeighing.some(
            (ws) => ws.gross_weight !== 0 || ws.tare_weight !== 0,
        );

        if (!hasWeighingSlipWithWeight) {
            deletePositionsHandler([positionInWeighing.id]);
        }
    }, [setWeighingModalState, positionInWeighing, deletePositionsHandler]);

    const handleWeighingSlipSelection = useCallback((orderPositionId: string, weighingSlipId: string | undefined) => {
        setWeighingModalState((state) => ({
            mode: state?.mode ?? EnumWeighing.Gross,
            orderPositionId,
            weighingSlipId,
        }));
        return true;
    }, []);

    const closeWeighingSlipSelectionModal = useCallback(() => {
        setWeighingModalState(undefined);
    }, []);

    return {
        startWeighingIn,
        startWeighingOut,
        weighInNewPosition,
        saveAndNavigateToHofliste,
        approveAndPrint,
        weighingModalState,
        closeWeighingModal,
        handleWeighingSlipSelection,
        closeWeighingSlipSelectionModal,
    };
}
